import IconBlueLogo from '@listening/assets/astro/icons/icon-blue-logo.svg?react';
import IconFacebook from '@listening/assets/astro/icons/icon-facebook-dark.svg?react';
import IconIstagram from '@listening/assets/astro/icons/icon-instagram-dark.svg?react';
import IconLinkedin from '@listening/assets/astro/icons/icon-linkedin-dark.svg?react';
import IconTikTok from '@listening/assets/astro/icons/icon-tiktok-dark.svg?react';
import IconYoutube from '@listening/assets/astro/icons/icon-youtube-dark.svg?react';
import { clientEnv } from '@/env';
import { isRouteEnabled } from '@/lib/route-availability';
import { Fragment, type FunctionComponent } from 'react';
import { LEGAL_LINKS, SOCIAL_LINKS } from '@/server/data/links';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';

type FooterLink = {
  title: string;
  href: string;
  badge?: string;
};

type FooterSubSection = {
  title: string;
  links: Array<FooterLink>;
};

type FooterColumn = {
  title: string;
  links: Array<FooterLink>;
  subSection?: FooterSubSection;
};

const publicToolsEnabled = clientEnv.publicToolsEnabled;

const SOCIAL_ICON_MAP: Record<
  string,
  FunctionComponent<{ className?: string }>
> = {
  Instagram: IconIstagram,
  Facebook: IconFacebook,
  TikTok: IconTikTok,
  LinkedIn: IconLinkedin,
  Youtube: IconYoutube,
};

const productLinks: Array<FooterLink> = publicToolsEnabled
  ? [
      { title: 'Text to speech', href: '/text-to-speech' },
      { title: 'PDF to audio', href: '/pdf-to-audio' },
      { title: 'Web page reader', href: '/web-page-reader' },
      { title: 'Voice generator', href: '/text-to-voice-generator' },
      { title: 'All tools', href: '/tools' },
    ]
  : [];

const platformLinks: Array<FooterLink> = [
  {
    title: 'iOS app',
    href: 'https://apps.apple.com/us/app/listening-text-to-speech/id1635430768',
  },
  {
    title: 'Android app',
    href: 'https://play.google.com/store/apps/details?id=io.listening.main',
  },
  {
    title: 'Chrome extension',
    href: 'https://chromewebstore.google.com/detail/listeningcom/amjjglmomeodglgclcnjdjbhhcfnbnpd',
  },
  ...(publicToolsEnabled
    ? [{ title: 'Extension page', href: '/browser-extension' }]
    : []),
];

const useCaseLinks: Array<FooterLink> = publicToolsEnabled
  ? [
      { title: 'Academic papers', href: '/academic-paper-reader' },
      { title: 'Students', href: '/text-to-speech-for-students' },
      { title: 'Dyslexia support', href: '/dyslexia-reading-assistant' },
      { title: 'ADHD reading', href: '/text-to-speech-for-adhd' },
      { title: 'Speed reading', href: '/speed-reading' },
      { title: 'Audio notes', href: '/audio-note-taking' },
    ]
  : [];

const languagesLinks: Array<FooterLink> = publicToolsEnabled
  ? [
      { title: 'English', href: '/english-text-to-speech' },
      { title: 'Spanish', href: '/spanish-text-to-speech' },
    ]
  : [];

const pdfLanguageLinks: Array<FooterLink> = publicToolsEnabled
  ? [
      { title: 'English PDF', href: '/pdf-to-audio' },
      { title: 'Spanish PDF', href: '/spanish-pdf-to-audio' },
    ]
  : [];

const resourcesLinks: Array<FooterLink> = [
  { title: 'Blog', href: '/blog' },
  { title: 'Help Center', href: 'https://help.listening.com' },
  { title: 'Contact us', href: '/contact-us' },
  ...(publicToolsEnabled ? [{ title: 'All tools', href: '/tools' }] : []),
  { title: 'Documents', href: '/doc' },
];

const companyLinks: Array<FooterLink> = [
  { title: 'Pricing', href: '/pricing' },
  { title: 'Business', href: '/business' },
  { title: 'Downloads', href: '/downloads' },
  { title: 'Features', href: '/features' },
  { title: 'Compare apps', href: '/text-to-speech-app-comparison' },
].filter((link) => isRouteEnabled(link.href, clientEnv.mode));

const columns: Array<FooterColumn> = publicToolsEnabled
  ? [
      {
        title: 'Tools',
        links: productLinks,
      },
      {
        title: 'Use cases',
        links: useCaseLinks,
      },
      {
        title: 'Apps',
        links: platformLinks,
      },
      {
        title: 'Languages',
        links: languagesLinks,
        subSection: {
          title: 'PDF audio',
          links: pdfLanguageLinks,
        },
      },
      {
        title: 'Company',
        links: companyLinks,
      },
    ]
  : [
      {
        title: 'Platforms',
        links: platformLinks,
      },
      {
        title: 'Company',
        links: companyLinks,
      },
    ];

const footerNavColumnsClass = publicToolsEnabled
  ? 'md:grid-cols-3 xl:grid-cols-5'
  : 'sm:grid-cols-2';

function FooterLinkItem({ link }: { link: FooterLink }) {
  const isExternal = link.href.startsWith('http');
  return (
    <li>
      <a
        className='inline-flex items-center gap-2 text-[15px] leading-tight text-[#0f0e0c] transition-colors hover:text-[#0f0e0c]/70'
        href={isExternal ? link.href : localizeHref(link.href)}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        target={isExternal ? '_blank' : undefined}
      >
        <span>{link.title}</span>
        {link.badge && (
          <span className='inline-flex h-[18px] items-center rounded-md bg-[#0f0e0c]/8 px-1.5 font-mono text-[10px] font-medium tracking-[0.04em] text-[#5a564e] uppercase'>
            {link.badge}
          </span>
        )}
      </a>
    </li>
  );
}

function FooterColumnView({ column }: { column: FooterColumn }) {
  return (
    <div className='flex flex-col gap-7'>
      <div>
        <h3 className='mb-5 font-mono text-[12px] font-medium tracking-[0.08em] text-[#8e887d] uppercase'>
          {column.title}
        </h3>
        <ul className='space-y-3'>
          {column.links.map((link) => (
            <FooterLinkItem key={link.href} link={link} />
          ))}
        </ul>
      </div>
      {column.subSection && (
        <div>
          <h3 className='mb-5 font-mono text-[12px] font-medium tracking-[0.08em] text-[#8e887d] uppercase'>
            {column.subSection.title}
          </h3>
          <ul className='space-y-3'>
            {column.subSection.links.map((link) => (
              <FooterLinkItem key={link.href} link={link} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function Footer({ noMarginTop }: { noMarginTop?: boolean }) {
  return (
    <footer
      aria-label={m.footer_aria_site_footer()}
      className={`bg-[#f6f2ea] text-[#0f0e0c]${noMarginTop ? '' : ''}`}
      role='contentinfo'
    >
      <div className='mx-auto w-full max-w-[1600px] px-6 pt-20 pb-10 sm:px-8 lg:px-12 lg:pt-24 lg:pb-12'>
        <div className='grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,3fr)] lg:gap-16'>
          <div className='flex flex-col'>
            <a
              aria-label={`${m.nav_brand_name()} home`}
              className='inline-flex max-w-fit items-center gap-3 text-[clamp(32px,3.6vw,52px)] leading-[0.95] font-bold tracking-[-0.035em] text-[#0f0e0c] no-underline sm:gap-4'
              href={localizeHref('/')}
            >
              <IconBlueLogo
                aria-label={`${m.nav_brand_name()} logo`}
                className='size-[clamp(32px,3.6vw,52px)]'
                role='img'
              />
              <span>{m.nav_brand_name()}</span>
            </a>
            <div className='mt-6 flex flex-wrap gap-2.5'>
              {SOCIAL_LINKS.map((link) => {
                const Icon = SOCIAL_ICON_MAP[link.title];
                return (
                  <a
                    aria-label={`${link.title} (opens in new tab)`}
                    className='inline-flex size-10 items-center justify-center rounded-full bg-[#0f0e0c]/8 text-[#0f0e0c] transition-colors hover:bg-[#0f0e0c]/15'
                    href={link.href}
                    key={link.href}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {Icon && (
                      <Icon aria-hidden='true' className='size-[18px]' />
                    )}
                  </a>
                );
              })}
            </div>
            <div className='mt-10'>
              <h3 className='mb-5 font-mono text-[12px] font-medium tracking-[0.08em] text-[#8e887d] uppercase'>
                Resources
              </h3>
              <ul className='space-y-3'>
                {resourcesLinks.map((link) => (
                  <FooterLinkItem key={link.href} link={link} />
                ))}
              </ul>
            </div>
          </div>

          <nav
            aria-label={m.footer_aria_navigation()}
            className={`grid grid-cols-2 gap-x-8 gap-y-12 md:gap-x-10 ${footerNavColumnsClass}`}
          >
            {columns.map((column) => (
              <FooterColumnView column={column} key={column.title} />
            ))}
          </nav>
        </div>

        <div
          aria-label={m.footer_aria_legal_info()}
          className='mt-20 flex flex-col items-start justify-between gap-4 border-t border-[#0f0e0c]/12 pt-8 md:flex-row md:items-center lg:mt-24'
        >
          <nav
            aria-label={m.footer_aria_legal_links()}
            className='flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[#5a564e]'
          >
            {LEGAL_LINKS.map((link, index) => (
              <Fragment key={link.href}>
                <a
                  className='transition-colors hover:text-[#0f0e0c]'
                  href={localizeHref(link.href)}
                >
                  {link.title}
                </a>
                {index < LEGAL_LINKS.length - 1 && (
                  <span aria-hidden='true' className='text-[#0f0e0c]/30'>
                    ·
                  </span>
                )}
              </Fragment>
            ))}
          </nav>
          <div className='text-[13px] text-[#5a564e]' role='contentinfo'>
            © {new Date().getFullYear()} {m.nav_brand_name()}.{' '}
            {m.footer_all_rights_reserved()}
          </div>
        </div>
      </div>
    </footer>
  );
}
